import { queryOptions, useQuery } from "@tanstack/react-query";
import {
  adminNotificationService,
  type StatusCountParams,
} from "@/services/adminNotificationService";

const STATUS_COUNTS_REFETCH_INTERVAL_MS = 45_000;

export const adminNotificationKeys = {
  all: ["admin-notifications"] as const,
  statusCounts: ({ severity, category }: StatusCountParams) =>
    [
      ...adminNotificationKeys.all,
      "status-counts",
      severity ?? null,
      category ?? null,
    ] as const,
};

function statusCountsQuery(filters: StatusCountParams) {
  return queryOptions({
    queryKey: adminNotificationKeys.statusCounts(filters),
    queryFn: () => adminNotificationService.getStatusCounts(filters),
  });
}

/**
 * Per-status notification counts — the single source for every "how many
 * alerts" number (sidebar badge, dashboard panel, inbox status tabs).
 * Readers do not poll: the unfiltered entry is kept fresh by
 * `usePolledAdminNotificationStatusCounts` and mutations invalidate the prefix.
 */
export function useAdminNotificationStatusCounts(
  filters: StatusCountParams,
  enabled: boolean,
) {
  return useQuery({ ...statusCountsQuery(filters), enabled });
}

/**
 * Unfiltered counts for the always-mounted sidebar badge, refetched on an
 * interval. Each observer runs its own interval timer, so this is the only
 * polling observer; the dashboard and inbox read the same cache entry.
 */
export function usePolledAdminNotificationStatusCounts(enabled: boolean) {
  return useQuery({
    ...statusCountsQuery({}),
    enabled,
    refetchInterval: STATUS_COUNTS_REFETCH_INTERVAL_MS,
  });
}
